import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { BarChart2, BookOpen, Users, Star, TrendingUp, Eye } from '../../components/CustomIcons';

function MiniBar({ value, max, color = 'var(--orange)' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ flex: 1, height: '6px', background: 'var(--bg-elevated)', borderRadius: '3px', overflow: 'hidden' }}>
      <div style={{ height: '100%', background: color, borderRadius: '3px', width: `${pct}%`, transition: 'width 0.6s ease' }} />
    </div>
  );
}

export default function ProviderAnalytics() {
  const { authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch('/marketplace/provider/analytics')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const { course_stats = [], totals = {} } = data || {};
  const maxEnrolled = Math.max(...course_stats.map(c => c.enrolled_count), 1);
  const maxInterest = Math.max(...course_stats.map(c => c.interest_count), 1);

  return (
    <div className="page-wrapper" style={{ padding: '24px', background: 'var(--bg-page)', minHeight: '100vh' }} data-testid="provider-analytics">
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        <div style={{ marginBottom: '28px' }}>
          <div className="overline" style={{ marginBottom: '4px' }}>Provider Portal</div>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '26px', color: 'var(--text-primary)', margin: 0 }}>Analytics</h1>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '14px', marginBottom: '28px' }}>
          {[
            { icon: BookOpen, label: 'Total Courses', value: totals.courses || 0, color: 'var(--orange)' },
            { icon: Users, label: 'Total Enrollments', value: (totals.enrollments || 0).toLocaleString(), color: '#3B82F6' },
            { icon: Eye, label: 'Total Interest', value: (totals.interest || 0).toLocaleString(), color: '#8B5CF6' },
          ].map(({ icon: Icon, label, value, color }) => (
            <div key={label} className="card" style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={17} style={{ color }} />
                </div>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'Inter, sans-serif' }}>{label}</span>
              </div>
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '28px', color: 'var(--text-primary)' }}>{value}</div>
            </div>
          ))}
        </div>

        {/* Per-Course Breakdown */}
        <div className="card" style={{ padding: '24px' }}>
          <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '17px', color: 'var(--text-primary)', margin: '0 0 20px 0' }}>
            Course Performance
          </h2>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: '72px', borderRadius: '10px' }} />)}
            </div>
          ) : course_stats.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
              <BarChart2 size={36} style={{ marginBottom: '12px', color: 'var(--text-muted)' }} />
              <p>No course data yet. Publish a course to see analytics.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {course_stats.map(course => (
                <div key={course.id} style={{ padding: '16px', background: 'var(--bg-elevated)', borderRadius: '12px', border: '1px solid var(--border-subtle)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)', margin: '0 0 3px 0' }}>
                        {course.title}
                      </p>
                      <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>{course.category}</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {course.rating > 0 && (
                        <span style={{ fontSize: '12px', color: 'var(--orange)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Star size={12} fill="var(--orange)" /> {course.rating}
                        </span>
                      )}
                      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: course.status === 'published' ? 'var(--success-bg)' : 'var(--warning-bg)', color: course.status === 'published' ? 'var(--success)' : 'var(--warning)', fontWeight: 600 }}>
                        {course.status}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)', width: '70px', flexShrink: 0 }}>Enrolled</span>
                      <MiniBar value={course.enrolled_count} max={maxEnrolled} color="var(--orange)" />
                      <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', width: '30px', textAlign: 'right', flexShrink: 0 }}>{course.enrolled_count}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '12px', color: 'var(--text-muted)', width: '70px', flexShrink: 0 }}>Interest</span>
                      <MiniBar value={course.interest_count} max={maxInterest} color="#8B5CF6" />
                      <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', width: '30px', textAlign: 'right', flexShrink: 0 }}>{course.interest_count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
